import type { OrganizationPerson } from '@repo/sdk/gen'
import { zOrganizationPerson } from '@repo/sdk/zod'
import type { TFunction, TKey } from '@/lib/i18n'

export type PersonStatus = OrganizationPerson['status']

/**
 * The four states a person can be in, read off the contract rather than
 * retyped beside it. The filter's options come from here, so a fifth value the
 * backend starts returning reaches the filter without anyone editing a second
 * list — and reaches the maps below as a compile error, which is the point.
 */
export const PERSON_STATUSES = zOrganizationPerson.shape.status.options

/**
 * I due ruoli che una persona può avere, letti dal contratto come gli stati
 * qui sopra e per la stessa ragione: un terzo ruolo aggiunto dal server
 * arriva al filtro senza che nessuno modifichi un secondo elenco, e arriva a
 * `PERSON_ROLE_LABEL_KEYS` come errore di compilazione finché non ha una
 * parola.
 *
 * `role` è nullable nel contratto — una persona rimossa non ne ha uno — e qui
 * si scarta il nullo: un filtro offre i ruoli che si possono chiedere, e
 * «nessun ruolo» si chiede dallo stato, non da questo menu (OPSSTD-8139).
 */
export const PERSON_ROLES = zOrganizationPerson.shape.role.unwrap().options

/**
 * Status → the word an administrator reads. **Total by construction**:
 * `satisfies Record<PersonStatus, TKey>` makes a status without a label a
 * compile error, so nothing downstream can meet one it has no reading for and
 * fall back to printing the enum. `TKey` checks the other end — a key that
 * does not exist in the catalogues does not typecheck either.
 *
 * The status is the backend's own reading and is never re-derived here. Two
 * edges depend on that and both are invisible from the other fields:
 *
 * - A BAN THAT HAS LAPSED reads `active` again. There is nothing to show for
 *   it, and a client-side reconstruction would keep saying "Disattivato".
 * - AN INVITATION THAT HAS RUN OUT OF TIME still reads `invited`. Expiry is
 *   not a fifth state: from the team's side that person was asked and has not
 *   come. It travels separately as `invitationExpiresAt`, and the invitation
 *   column is what makes the staleness visible.
 */
export const PERSON_STATUS_LABEL_KEYS = {
	active: 'admin-people:status.active',
	invited: 'admin-people:status.invited',
	deactivated: 'admin-people:status.deactivated',
	removed: 'admin-people:status.removed'
} as const satisfies Record<PersonStatus, TKey>

/**
 * Role → its word, over the two roles that exist. `null` is not in the map on
 * purpose: it means "holds no role", which is what being removed *is*, and the
 * cell renders it as a blank rather than looking a label up for it.
 *
 * Neither role grants access to any file — that comes from sharing and only
 * from sharing — so neither label may suggest reach over content.
 */
export const PERSON_ROLE_LABEL_KEYS = {
	org_admin: 'admin-people:role.org_admin',
	member: 'admin-people:role.member'
} as const satisfies Record<NonNullable<OrganizationPerson['role']>, TKey>

/** Role → one line on what it does, read under each option of the role picker. */
export const PERSON_ROLE_DESCRIPTION_KEYS = {
	org_admin: 'admin-people:role_description.org_admin',
	member: 'admin-people:role_description.member'
} as const satisfies Record<NonNullable<OrganizationPerson['role']>, TKey>

/**
 * The one word a confirmation calls somebody by.
 *
 * Name first and address second, which is the order the list's own identifying
 * column uses: somebody who has not arrived yet has no name — one is chosen
 * when the invitation is accepted — so the address stands in for it. A row with
 * neither is what the contract allows for an account carrying no address at
 * all, and a dialog titled with an empty gap is worse than one that says the
 * name is not available.
 */
export function personLabel(person: OrganizationPerson, t: TFunction) {
	return person.name ?? person.email ?? t('admin-people:no_value')
}

/**
 * Status → which of the three acts on a person's access the row offers.
 *
 * Total by construction, like the two maps above: a fifth status the contract
 * starts returning is a compile error here rather than a row that silently
 * offers whatever the last branch happened to cover.
 *
 * `invited` offers nothing, and that is the decision worth reading twice. The
 * calls would work — a person has an id from the moment they are invited — but
 * the act for somebody who was asked and has not come is to take the invitation
 * back, which is the other group in the same menu. The endpoint says the same
 * about itself: an administrator meets removal and revocation in different
 * places, one on a row reading `active` and the other on a row reading
 * `invited`, and offering both on one row is how the wrong one gets used.
 *
 * `removed` offers nothing because there is nothing left to offer: reactivation
 * is refused for a removed person, and removing them again changes nothing.
 */
export const PERSON_LIFECYCLE_ACTIONS = {
	active: 'bar',
	invited: 'none',
	deactivated: 'restore',
	removed: 'none'
} as const satisfies Record<PersonStatus, 'bar' | 'restore' | 'none'>
